import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/785352e4-5f12-4aeb-9625-8cdfbebc5315.png" alt="ROD S.R.L. Logo" className="h-10 w-10 object-contain" />
              <span className={`text-xl font-semibold tracking-tight ${scrolled ? 'text-gray-900' : 'text-white'}`}>ROD S.R.L.</span>
            </div>
            <div className="flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-purple-600' : 'text-white/90 hover:text-white'}`}>Home</button>
              <button onClick={() => scrollToSection('about')} className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-purple-600' : 'text-white/90 hover:text-white'}`}>About</button>
              <button onClick={() => scrollToSection('products')} className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-purple-600' : 'text-white/90 hover:text-white'}`}>Products</button>
              <button onClick={() => scrollToSection('features')} className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-purple-600' : 'text-white/90 hover:text-white'}`}>Features</button>
              <button onClick={() => scrollToSection('contact')} className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-purple-600' : 'text-white/90 hover:text-white'}`}>Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/f1163aac41a8953fcd89bb4d0138e48a.jpg" alt="Hero Background" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <h1 className="text-7xl md:text-8xl font-light text-white mb-2 tracking-wide">Modern iOS Applications</h1>
          <h2 className="text-7xl md:text-8xl font-bold text-white mb-16 tracking-wide">Built for Performance</h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">Expert team developing VPN and Cleaner solutions that enhance security and optimize mobile device performance</p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-6">WHO WE ARE</p>
            <h3 className="text-5xl font-bold text-gray-900 mb-8 font-serif">ROD S.R.L.</h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">We are a dedicated team of experts specializing in developing modern applications for the iOS platform. Our commitment to excellence drives us to create innovative solutions that enhance user experience and improve the security of mobile devices.</p>
            <p className="text-lg text-gray-700 leading-relaxed">With years of experience in iOS development, we focus on delivering high-quality products that meet the evolving needs of our users. Our flagship products, VPN and Cleaner, are designed with cutting-edge technology to provide seamless performance and robust security.</p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <p className="text-sm font-bold text-purple-600 uppercase tracking-widest mb-5">OUR PRODUCTS</p>
            <h3 className="text-5xl font-bold text-gray-900 mb-4">VPN & Cleaner Solutions</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Powerful iOS applications designed to protect your privacy and optimize your device performance</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* VPN Product Card */}
            <div className="bg-white rounded-3xl p-12 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-8">
                <i className="ri-shield-keyhole-line text-white text-4xl"></i>
              </div>
              <h4 className="text-3xl font-bold text-gray-900 mb-6">VPN</h4>
              <ul className="space-y-3 mb-10">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-purple-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Military-grade encryption for maximum security</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-purple-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Global server network for fast connections</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-purple-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">No-logs policy to protect your privacy</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-purple-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">One-tap connection for instant protection</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-purple-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Automatic kill switch for enhanced safety</span>
                </li>
              </ul>
              <span className="inline-block bg-purple-100 text-purple-700 text-sm font-bold px-4 py-2 rounded-full whitespace-nowrap">iOS Optimized</span>
            </div>

            {/* Cleaner Product Card */}
            <div className="bg-white rounded-3xl p-12 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mb-8">
                <i className="ri-broom-line text-white text-4xl"></i>
              </div>
              <h4 className="text-3xl font-bold text-gray-900 mb-6">Cleaner</h4>
              <ul className="space-y-3 mb-10">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-purple-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Smart storage management and optimization</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-purple-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Remove duplicate photos and videos</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-purple-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Clear cache and temporary files safely</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-purple-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Battery optimization for longer life</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-purple-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Intuitive interface for easy cleaning</span>
                </li>
              </ul>
              <span className="inline-block bg-purple-100 text-purple-700 text-sm font-bold px-4 py-2 rounded-full whitespace-nowrap">iOS Optimized</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-24">
            <span className="inline-block bg-purple-100 text-purple-700 text-xs font-semibold uppercase tracking-wider px-5 py-3 rounded-full mb-7">WHY CHOOSE US</span>
            <h3 className="text-6xl font-bold text-gray-900 leading-tight">Security & Performance<br />In Every Detail</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature Card 1 */}
            <div className="border border-gray-200 rounded-2xl p-9 hover:border-purple-300 transition-colors duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center mb-6">
                <i className="ri-lock-password-line text-purple-600 text-3xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Military-Grade Encryption</h4>
              <p className="text-gray-600 leading-relaxed">Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide to ensure maximum privacy.</p>
            </div>

            {/* Feature Card 2 */}
            <div className="border border-gray-200 rounded-2xl p-9 hover:border-purple-300 transition-colors duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center mb-6">
                <i className="ri-flashlight-line text-blue-600 text-3xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">One-Tap Optimization</h4>
              <p className="text-gray-600 leading-relaxed">Simplify your device maintenance with intelligent one-tap cleaning that automatically identifies and removes unnecessary files to free up valuable storage space.</p>
            </div>

            {/* Feature Card 3 */}
            <div className="border border-gray-200 rounded-2xl p-9 hover:border-purple-300 transition-colors duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-green-600 text-3xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Privacy Protection</h4>
              <p className="text-gray-600 leading-relaxed">We maintain a strict no-logs policy, ensuring that your online activities remain completely private and are never tracked, stored, or shared with third parties.</p>
            </div>

            {/* Feature Card 4 */}
            <div className="border border-gray-200 rounded-2xl p-9 hover:border-purple-300 transition-colors duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center mb-6">
                <i className="ri-folder-settings-line text-orange-600 text-3xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Smart Storage Management</h4>
              <p className="text-gray-600 leading-relaxed">Advanced algorithms analyze your device to identify duplicate files, large attachments, and unused apps, helping you reclaim storage efficiently.</p>
            </div>

            {/* Feature Card 5 */}
            <div className="border border-gray-200 rounded-2xl p-9 hover:border-purple-300 transition-colors duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-100 to-lime-100 flex items-center justify-center mb-6">
                <i className="ri-battery-charge-line text-yellow-600 text-3xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Battery Efficiency</h4>
              <p className="text-gray-600 leading-relaxed">Our apps are optimized to minimize battery consumption while maintaining peak performance, ensuring your device lasts longer throughout the day.</p>
            </div>

            {/* Feature Card 6 */}
            <div className="border border-gray-200 rounded-2xl p-9 hover:border-purple-300 transition-colors duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center mb-6">
                <i className="ri-advertisement-line text-pink-600 text-3xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Ad-Free Experience</h4>
              <p className="text-gray-600 leading-relaxed">Enjoy a clean, distraction-free interface without intrusive advertisements, allowing you to focus on what matters most - your privacy and device performance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h3 className="text-4xl font-bold text-white mb-6">Get In Touch</h3>
              <p className="text-lg text-white/90 leading-relaxed mb-12">Have questions about our products or need support? We're here to help you enhance your iOS experience.</p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-white/70 uppercase tracking-wider mb-1">Address</p>
                    <p className="text-white font-medium">Italia 2717</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-white/70 uppercase tracking-wider mb-1">City</p>
                    <p className="text-white font-medium">Córdoba</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-white/70 uppercase tracking-wider mb-1">Zip</p>
                    <p className="text-white font-medium">X5009BTE</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-global-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-white/70 uppercase tracking-wider mb-1">Country</p>
                    <p className="text-white font-medium">Argentina</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-center">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-send-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-white/70 uppercase tracking-wider mb-1">Email</p>
                    <p className="text-white font-medium text-lg">help@rodsrl.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-white/70 uppercase tracking-wider mb-1">Number</p>
                    <p className="text-white font-medium text-lg">+1 7018074111</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-400">© 2025 ROD S.R.L. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Powered by Readdy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}